/**
 * Regras puras do vínculo evento do extrato × fornecedor (conciliação Itaú).
 * A identificação do fornecedor e a busca das combinações são do backend;
 * aqui só se decide como mostrar e como montar a decisão.
 */
import type {
  BankStatementDecisionItem,
  BankStatementPreviewItem,
  SupplierMatchSource,
  SupplierPaymentCandidate,
  SupplierPaymentSuggestion,
} from '@/types/bankIntegration';
import { formatCurrencyBRL } from './financialUtils';

export const SUPPLIER_SOURCE_LABEL: Record<SupplierMatchSource, string> = {
  document: 'pelo CNPJ/CPF da contraparte',
  counterpart: 'contraparte aprendida',
  name: 'pelo nome da contraparte',
  '': '',
};

/** Chave estável de um candidato (lançamento e parcela podem ter o mesmo id). */
export const candidateKey = (c: Pick<SupplierPaymentCandidate, 'kind' | 'id'>): string => `${c.kind}:${c.id}`;

/** "PC #76 · 2/2" para parcela; "#422486 · PC #76" para lançamento. */
export const candidateLabel = (c: SupplierPaymentCandidate): string => {
  if (c.kind === 'parcela') return `${c.documentLabel ?? 'PC'} · ${c.installmentLabel ?? ''}`.trim();
  return c.documentLabel ? `#${c.id} · ${c.documentLabel}` : `#${c.id}`;
};

/** "PC #71 · #422481 R$ 2.147,50 + PC #76 · #422486 R$ 2.820,00" */
export const suggestionLabel = (s: SupplierPaymentSuggestion): string =>
  s.items.map(i => `${candidateLabel(i)} ${formatCurrencyBRL(i.amountCents / 100)}`).join(' + ');

/** "2 lançamentos existentes" / "parcela em aberto". */
export const suggestionKindLabel = (s: SupplierPaymentSuggestion): string => {
  const n = s.items.length;
  if (s.kind === 'lancamentos') return n === 1 ? 'lançamento existente' : `${n} lançamentos existentes`;
  return n === 1 ? 'parcela em aberto' : `${n} parcelas em aberto`;
};

/** Soma (centavos) dos candidatos selecionados. */
export const selectionTotalCents = (cands: SupplierPaymentCandidate[], selected: Set<string>): number =>
  cands.reduce((sum, c) => (selected.has(candidateKey(c)) ? sum + c.amountCents : sum), 0);

/** A seleção fecha o valor do evento ao centavo? (o backend confere de novo) */
export const selectionMatches = (totalCents: number, eventAmountCents: number): boolean =>
  totalCents > 0 && totalCents === Math.abs(eventAmountCents);

export const selectionHasInstallments = (cands: SupplierPaymentCandidate[], selected: Set<string>): boolean =>
  cands.some(c => c.kind === 'parcela' && selected.has(candidateKey(c)));

/**
 * Aprender a contraparte por padrão quando o banco informou um documento e o
 * fornecedor NÃO foi identificado por ele (vínculo manual, ou identificado só
 * pelo nome) — é o CPF da sócia que ninguém cadastrou.
 */
export const shouldLearnByDefault = (item: Pick<BankStatementPreviewItem, 'counterpartDocument' | 'supplier'>): boolean =>
  Boolean(item.counterpartDocument) && item.supplier?.source !== 'document' && item.supplier?.source !== 'counterpart';

/** Monta a decisão 'supplier' a partir da seleção. */
export const buildSupplierDecision = (
  item: BankStatementPreviewItem,
  providerId: number,
  selected: SupplierPaymentCandidate[],
  categoryErpId: number | null,
  learnCounterpart: boolean,
): BankStatementDecisionItem => ({
  providerEventId: item.providerEventId,
  date: item.date,
  description: item.description,
  amountCents: item.amountCents,
  decision: 'supplier',
  accountingPlanErpId: selected.some(c => c.kind === 'parcela') ? categoryErpId : null,
  supplierLink: {
    providerId,
    transactionIds: selected.filter(c => c.kind === 'lancamento').map(c => c.id),
    installmentIds: selected.filter(c => c.kind === 'parcela').map(c => c.id),
    learnCounterpart,
    counterpart: item.counterpart ?? '',
    counterpartDocument: item.counterpartDocument ?? '',
  },
});
